import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import {
  RefreshCw,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Database,
  Globe,
  ChevronDown,
  ChevronUp,
  Eye,
  Users,
  Building2,
  Lock,
  MessageSquare,
  MousePointerClick,
  Bookmark,
  FileText,
  Copy,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import LoadingLogo from "../../../components/LoadingLogo";
import { AuthContext } from "../../../contexts/AuthContext";
import { getCriteriaSelectionLines } from "../../../features/search/utils/historyCriteria";
import "../../../index.css";
import { LOGS_TRANSLATIONS } from "./translations";
import {
  AdminQueryLogAuthError,
  exportSearchLogs as exportSearchLogsRequest,
  fetchPaperAnalysisEngagement,
  fetchPaperAnalysisThread,
  fetchSearchEngagement,
  fetchSearchLogs,
} from "./api";
import {
  buildUtcRangeForDateSelection,
  buildUtcRangeForLocalDateRange,
  downloadBlob,
  formatCost,
  formatCredits as formatCreditsValue,
  formatDuration as formatDurationValue,
  formatPreference,
  formatSearchMode as formatSearchModeValue,
  formatTimestamp as formatTimestampValue,
  getLocalYYYYMMDD,
  hasPaperAnalysisEngagementEvents,
  hasSearchEngagementEvents,
  normalizePaperPmid,
  resolveDownloadFilename,
  resolveLogRowId,
  resolvePaperAnalysisEngagementKey,
  resolvePaperAnalysisKey,
  resolveSearchEngagementKey,
} from "./utils";

function QueryLogsPage() {
  const { user } = useContext(AuthContext);
  const [logs, setLogs] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const loadingRef = useRef(false);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [paperAnalysisThreads, setPaperAnalysisThreads] = useState({});
  const [searchEngagementByKey, setSearchEngagementByKey] = useState({});
  const [paperAnalysisEngagementByKey, setPaperAnalysisEngagementByKey] =
    useState({});
  const [groupBy, setGroupBy] = useState("company"); // 'none', 'user', 'company'
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("pubmed-search-language") || "ja";
  });
  const navigate = useNavigate();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Check if user is admin, redirect if not
  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/");
    }
  }, [user, navigate]);

  // Filter states
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterQuery, setFilterQuery] = useState("");
  const [filterStartDate, setFilterStartDate] = useState(() =>
    getLocalYYYYMMDD(new Date()),
  );
  const [filterEndDate, setFilterEndDate] = useState(() =>
    getLocalYYYYMMDD(new Date()),
  );

  const t = LOGS_TRANSLATIONS[language];

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem("pubmed-search-language", language);
  }, [language]);

  const loadLogs = useCallback(
    async (page = currentPage, itemsPerPage = perPage) => {
      if (!user || user.role !== "admin") return;

      // Prevent multiple simultaneous loads
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      try {
        const todayDate = getLocalYYYYMMDD(new Date());
        const targetStartDate = filterStartDate || todayDate;
        const targetEndDate = filterEndDate || targetStartDate;

        const { start, end, startDate, endDate } = buildUtcRangeForLocalDateRange(
          targetStartDate,
          targetEndDate,
        );
        if (!filterStartDate) {
          setFilterStartDate(startDate);
        }
        if (!filterEndDate) {
          setFilterEndDate(endDate);
        }

        const data = await fetchSearchLogs({
          page,
          perPage: itemsPerPage,
          start,
          end,
        });

        if (data.status === "success") {
          // Handle new paginated response format
          const logsData = data.logs || [];
          const meta = data.meta || {};
          const analyticsData = data.analytics || {};

          setLogs(logsData);
          setAnalytics(analyticsData);

          // Update pagination state
          setTotalLogs(meta.total || 0);
          setTotalPages(meta.total_pages || 0);
          setCurrentPage(meta.page || page);
          setPerPage(meta.per_page || itemsPerPage);
        }
      } catch (error) {
        if (error instanceof AdminQueryLogAuthError) {
          navigate("/login");
        }
        alert(`Error loading logs: ${error.message}`);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [user, navigate, filterStartDate, filterEndDate, currentPage, perPage],
  );

  // Load logs when filters change (reset to page 1)
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    } else {
      loadLogs(1, perPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterStatus,
    filterQuery,
    filterStartDate,
    filterEndDate,
    perPage,
  ]);

  // Load logs when page or perPage changes
  useEffect(() => {
    loadLogs(currentPage, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage]);

  const refreshLogs = async () => {
    await loadLogs(currentPage, perPage);
    // Show success message briefly
    const originalTitle = document.title;
    document.title = t.logsRefreshed;
    setTimeout(() => {
      document.title = originalTitle;
    }, 2000);
  };

  const setDateFilter = (type) => {
    const today = new Date();

    if (type === "today") {
      const todayDate = getLocalYYYYMMDD(today);
      setFilterStartDate(todayDate);
      setFilterEndDate(todayDate);
    } else if (type === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayDate = getLocalYYYYMMDD(yesterday);
      setFilterStartDate(yesterdayDate);
      setFilterEndDate(yesterdayDate);
    }
  };

  const buildUtcRangeForExport = () =>
    buildUtcRangeForDateSelection(filterStartDate, filterEndDate);

  const exportSearchLogs = async () => {
    if (!user || user.role !== "admin") return;
    setExporting(true);
    try {
      const { start, end, startDate, endDate } = buildUtcRangeForExport();
      const response = await exportSearchLogsRequest({ start, end });
      const blob = await response.blob();
      const filename = resolveDownloadFilename(
        response.headers,
        `query_logs_${startDate}_to_${endDate}.zip`,
      );
      downloadBlob(blob, filename, response.headers.get("content-type"));
    } catch (error) {
      if (error instanceof AdminQueryLogAuthError) {
        navigate("/login");
      }
      alert(`Error exporting logs: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const formatTimestamp = (timestamp) => formatTimestampValue(timestamp, language);

  const formatDuration = (seconds) => formatDurationValue(seconds, t);

  const formatSearchMode = (value) => formatSearchModeValue(value, t);

  const formatCredits = (value) => formatCreditsValue(value, language);

  const renderPaperAiDetails = (paper) => {
    if (!paper) return null;
    const authors = Array.isArray(paper.authors) ? paper.authors.join(", ") : paper.authors;
    const imageCount = Array.isArray(paper.image_urls) ? paper.image_urls.length : 0;

    const fields = [
      { label: t.aiResearchQuestion, value: paper.research_question },
      { label: t.aiDesign, value: paper.design },
      { label: t.aiPopulation, value: paper.population },
      { label: t.aiExposureIntervention, value: paper.exposure_intervention },
      { label: t.aiEndpoint, value: paper.endpoint },
      { label: t.aiStatistics, value: paper.statistics },
      { label: t.aiKeyResults, value: paper.key_results },
      { label: t.aiRationale, value: paper.rationale },
      { label: t.aiJournal, value: paper.journal },
      { label: t.aiYear, value: paper.pub_year || paper.year },
      { label: t.aiAuthors, value: authors },
      { label: t.aiPmid, value: paper.pmid },
      { label: t.aiPmcid, value: paper.pmcid },
      { label: t.aiCitedBy, value: paper.cited_by },
      { label: t.aiImages, value: imageCount > 0 ? imageCount : "" },
    ];

    const hasDetails = fields.some(
      (field) =>
        field.value !== undefined &&
        field.value !== null &&
        String(field.value).trim() !== "",
    );

    if (!hasDetails) return null;

    return (
      <details className="bg-white border border-gray-200 rounded">
        <summary className="px-2 py-1 text-xs font-medium text-gray-700 cursor-pointer hover:bg-gray-50">
          {t.aiDetails}
        </summary>
        <div className="px-2 py-2 border-t border-gray-200 space-y-2">
          {fields.map((field, idx) => {
            if (
              field.value === undefined ||
              field.value === null ||
              String(field.value).trim() === ""
            ) {
              return null;
            }
            return (
              <div key={idx} className="text-xs text-gray-700">
                <span className="font-semibold text-gray-600 mr-1">
                  {field.label}:
                </span>
                <span className="break-words">{field.value}</span>
              </div>
            );
          })}
        </div>
      </details>
    );
  };

  const loadPaperAnalysisThread = useCallback(
    async (log) => {
      if (!log.user_id || !log.session_id) return;

      const threadKey = resolvePaperAnalysisKey(log);
      if (log.paper_analysis_question_count == null) {
        setPaperAnalysisThreads((prev) => ({
          ...prev,
          [threadKey]: {
            loading: false,
            error: null,
            thread: null,
            notFound: true,
          },
        }));
        return;
      }

      const current = paperAnalysisThreads[threadKey];
      if (current?.loading || current?.thread || current?.notFound) return;

      setPaperAnalysisThreads((prev) => ({
        ...prev,
        [threadKey]: { loading: true, error: null, thread: null },
      }));

      try {
        const result = await fetchPaperAnalysisThread({
          userId: log.user_id,
          searchRunId: log.session_id,
        });
        if (result.notFound) {
          setPaperAnalysisThreads((prev) => ({
            ...prev,
            [threadKey]: {
              loading: false,
              error: null,
              thread: null,
              notFound: true,
            },
          }));
          return;
        }

        setPaperAnalysisThreads((prev) => ({
          ...prev,
          [threadKey]: {
            loading: false,
            error: null,
            thread: result.data.thread || null,
          },
        }));
      } catch (error) {
        setPaperAnalysisThreads((prev) => ({
          ...prev,
          [threadKey]: {
            loading: false,
            error: error.message || t.paperAnalysisLoadFailed,
            thread: null,
          },
        }));
      }
    },
    [paperAnalysisThreads, t.paperAnalysisLoadFailed],
  );

  const loadSearchEngagement = useCallback(
    async (log) => {
      const searchId = log.session_id;
      const engagementKey = resolveSearchEngagementKey(log);
      if (!log.user_id || !searchId) {
        setSearchEngagementByKey((prev) => ({
          ...prev,
          [engagementKey]: {
            loading: false,
            error: t.engagementUnavailable,
            data: null,
          },
        }));
        return;
      }

      const current = searchEngagementByKey[engagementKey];
      if (current?.loading || current?.data || current?.notFound) return;

      setSearchEngagementByKey((prev) => ({
        ...prev,
        [engagementKey]: { loading: true, error: null, data: null },
      }));

      try {
        const result = await fetchSearchEngagement({
          userId: log.user_id,
          searchId,
        });
        if (result.notFound) {
          setSearchEngagementByKey((prev) => ({
            ...prev,
            [engagementKey]: {
              loading: false,
              error: null,
              data: null,
              notFound: true,
            },
          }));
          return;
        }

        setSearchEngagementByKey((prev) => ({
          ...prev,
          [engagementKey]: {
            loading: false,
            error: null,
            data: result.data,
          },
        }));
      } catch (error) {
        setSearchEngagementByKey((prev) => ({
          ...prev,
          [engagementKey]: {
            loading: false,
            error: error.message || t.engagementLoadFailed,
            data: null,
          },
        }));
      }
    },
    [searchEngagementByKey, t.engagementLoadFailed, t.engagementUnavailable],
  );

  const loadPaperAnalysisEngagement = useCallback(
    async (log) => {
      const searchRunId = log.session_id;
      const engagementKey = resolvePaperAnalysisEngagementKey(log);
      if (!log.user_id || !searchRunId) {
        setPaperAnalysisEngagementByKey((prev) => ({
          ...prev,
          [engagementKey]: {
            loading: false,
            error: t.engagementUnavailable,
            data: null,
          },
        }));
        return;
      }

      const current = paperAnalysisEngagementByKey[engagementKey];
      if (current?.loading || current?.data || current?.notFound) return;

      setPaperAnalysisEngagementByKey((prev) => ({
        ...prev,
        [engagementKey]: { loading: true, error: null, data: null },
      }));

      try {
        const result = await fetchPaperAnalysisEngagement({
          userId: log.user_id,
          searchRunId,
        });
        if (result.notFound) {
          setPaperAnalysisEngagementByKey((prev) => ({
            ...prev,
            [engagementKey]: {
              loading: false,
              error: null,
              data: null,
              notFound: true,
            },
          }));
          return;
        }

        setPaperAnalysisEngagementByKey((prev) => ({
          ...prev,
          [engagementKey]: {
            loading: false,
            error: null,
            data: result.data,
          },
        }));
      } catch (error) {
        setPaperAnalysisEngagementByKey((prev) => ({
          ...prev,
          [engagementKey]: {
            loading: false,
            error: error.message || t.engagementLoadFailed,
            data: null,
          },
        }));
      }
    },
    [paperAnalysisEngagementByKey, t.engagementLoadFailed, t.engagementUnavailable],
  );

  const toggleRowExpansion = (log, rowId) => {
    const shouldLoadExpandedData = !expandedRows.has(rowId);
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
    if (shouldLoadExpandedData) {
      loadPaperAnalysisThread(log);
      loadSearchEngagement(log);
      loadPaperAnalysisEngagement(log);
    }
  };

  const renderPaperAnalysisMessages = (log) => {
    const threadState = paperAnalysisThreads[resolvePaperAnalysisKey(log)];
    const paperAnalysisEngagement = getPaperAnalysisEngagementState(log)?.data;
    if (threadState?.loading) {
      return <LoadingLogo size="sm" message={t.paperAnalysis} />;
    }
    if (threadState?.error) {
      return (
        <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-900 break-words">
          {t.paperAnalysisLoadFailed}
        </div>
      );
    }

    const messages = Array.isArray(threadState?.thread?.messages)
      ? threadState.thread.messages
      : [];
    if (threadState?.notFound || messages.length === 0) {
      return (
        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-500">
          {t.paperAnalysisEmpty}
        </div>
      );
    }

    const turns = [];
    for (let index = 0; index < messages.length; index += 1) {
      const message = messages[index];
      if (message.role !== "user") continue;
      const nextMessage = messages[index + 1];
      turns.push({
        question: message,
        answer: nextMessage?.role === "assistant" ? nextMessage : null,
      });
    }

    if (turns.length === 0) {
      return (
        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-500">
          {t.paperAnalysisEmpty}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {turns.map((turn, index) => (
          <div key={`${turn.question.sequence}-${index}`} className="bg-white border border-gray-200 rounded">
            <div className="p-3 border-b border-gray-100">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                {t.paperAnalysisQuestion}
              </div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {turn.question.message_content}
              </div>
              {renderPaperAnalysisRefs(turn.question.paper_refs)}
            </div>
            <div className="p-3">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                {t.paperAnalysisAnswer}
              </div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {turn.answer?.message_content || ""}
              </div>
              {turn.answer && renderPaperAnalysisMessageEngagement(
                paperAnalysisEngagement,
                turn.answer.sequence,
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPaperAnalysisRefs = (paperRefs = []) => {
    if (!Array.isArray(paperRefs) || paperRefs.length === 0) return null;

    return (
      <div className="mt-3 rounded border border-gray-100 bg-gray-50 p-2">
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
          {t.paperAnalysisSelectedRefs}
        </div>
        <div className="flex flex-wrap gap-2">
          {paperRefs.map((ref, index) => {
            const labelParts = [];
            if (ref.pmid) labelParts.push(`PMID ${ref.pmid}`);
            if (ref.pmcid) labelParts.push(ref.pmcid);
            if (!labelParts.length && ref.url) labelParts.push(ref.url);
            const label = labelParts.join(" / ") || `Ref ${index + 1}`;
            return (
              <span
                key={`${ref.pmid || ""}-${ref.pmcid || ""}-${ref.url || ""}-${index}`}
                className="inline-flex max-w-full items-center rounded bg-white px-2 py-1 text-xs text-gray-700 ring-1 ring-gray-200"
                title={ref.url || label}
              >
                <span className="truncate">{label}</span>
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  const getSearchEngagementState = (log) =>
    searchEngagementByKey[resolveSearchEngagementKey(log)];

  const getPaperAnalysisEngagementState = (log) =>
    paperAnalysisEngagementByKey[resolvePaperAnalysisEngagementKey(log)];

  const renderEngagementMetric = (label, value, Icon) => (
    <div className="rounded border border-gray-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-gray-900">
        {value ?? 0}
      </div>
    </div>
  );

  const findPaperAnalysisMessageEngagement = (engagement, sequence) => {
    const messages = Array.isArray(engagement?.messages) ? engagement.messages : [];
    return (
      messages.find((message) => Number(message.sequence) === Number(sequence)) ||
      null
    );
  };

  const renderPaperAnalysisMessageEngagement = (engagement, sequence) => {
    const messageEngagement = findPaperAnalysisMessageEngagement(engagement, sequence);
    if (!messageEngagement) return null;

    return (
      <div className="mt-3 flex flex-wrap gap-1.5">
        <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
          {t.copies}: {messageEngagement.copies || 0}
        </span>
        <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
          {t.wordExports}: {messageEngagement.word_exports || 0}
        </span>
        <span className="inline-flex items-center rounded bg-green-50 px-2 py-0.5 text-xs text-green-700">
          {t.feedbackUp}: {messageEngagement.feedback_up || 0}
        </span>
        <span className="inline-flex items-center rounded bg-red-50 px-2 py-0.5 text-xs text-red-700">
          {t.feedbackDown}: {messageEngagement.feedback_down || 0}
        </span>
        {messageEngagement.latest_feedback_text && (
          <span
            className="inline-flex max-w-full items-center rounded bg-red-50 px-2 py-0.5 text-xs text-red-700"
            title={messageEngagement.latest_feedback_text}
          >
            <span className="truncate">
              {t.latestFeedback}: {messageEngagement.latest_feedback_text}
            </span>
          </span>
        )}
      </div>
    );
  };

  const renderPaperAnalysisEngagementSummary = (log) => {
    const engagementState = getPaperAnalysisEngagementState(log);
    if (engagementState?.loading) {
      return <LoadingLogo size="sm" message={t.paperAnalysisEngagement} />;
    }
    if (engagementState?.error) {
      return (
        <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-900 break-words">
          {engagementState.error || t.engagementLoadFailed}
        </div>
      );
    }

    const engagement = engagementState?.data;
    if (
      engagementState?.notFound ||
      !hasPaperAnalysisEngagementEvents(engagement)
    ) {
      return (
        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-500">
          {t.paperAnalysisEngagementEmpty}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {renderEngagementMetric(t.copies, engagement.copies, Copy)}
          {renderEngagementMetric(t.wordExports, engagement.word_exports, FileText)}
          {renderEngagementMetric(t.feedbackUp, engagement.feedback_up, CheckCircle)}
          {renderEngagementMetric(t.feedbackDown, engagement.feedback_down, XCircle)}
        </div>
        {engagement.last_event_at && (
          <div className="text-xs text-gray-500">
            {t.lastEvent}: {formatTimestamp(engagement.last_event_at)}
          </div>
        )}
      </div>
    );
  };

  const renderSearchEngagementSummary = (log) => {
    const engagementState = getSearchEngagementState(log);
    if (engagementState?.loading) {
      return <LoadingLogo size="sm" message={t.engagement} />;
    }
    if (engagementState?.error) {
      return (
        <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-900 break-words">
          {engagementState.error || t.engagementLoadFailed}
        </div>
      );
    }

    const engagement = engagementState?.data;
    if (engagementState?.notFound || !hasSearchEngagementEvents(engagement)) {
      return (
        <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-500">
          {t.engagementEmpty}
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {renderEngagementMetric(t.papersSeen, engagement.papers_seen, Eye)}
          {renderEngagementMetric(t.titleClicks, engagement.title_clicks, MousePointerClick)}
          {renderEngagementMetric(t.fullTextClicks, engagement.full_text_clicks, FileText)}
          {renderEngagementMetric(t.bookmarkAdds, engagement.bookmark_adds, Bookmark)}
          {renderEngagementMetric(t.bookmarkRemoves, engagement.bookmark_removes, Bookmark)}
          {renderEngagementMetric(t.csvExports, engagement.csv_exports, FileText)}
          {renderEngagementMetric(t.historyOpens, engagement.history_opens, Clock)}
        </div>
        {Number(engagement.history_opens || 0) > 0 && (
          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
            <span className="rounded bg-gray-100 px-2 py-1">
              {t.sidebarHistoryOpens}: {engagement.sidebar_history_opens || 0}
            </span>
            <span className="rounded bg-gray-100 px-2 py-1">
              {t.detailHistoryOpens}: {engagement.detail_history_opens || 0}
            </span>
            {engagement.last_history_open_at && (
              <span className="rounded bg-gray-100 px-2 py-1">
                {t.lastHistoryOpen}: {formatTimestamp(engagement.last_history_open_at)}
              </span>
            )}
          </div>
        )}
        {engagement.last_event_at && (
          <div className="text-xs text-gray-500">
            {t.lastEvent}: {formatTimestamp(engagement.last_event_at)}
          </div>
        )}
      </div>
    );
  };

  const findPaperEngagement = (log, paper) => {
    const engagement = getSearchEngagementState(log)?.data;
    const papers = Array.isArray(engagement?.papers) ? engagement.papers : [];
    if (!papers.length) return null;

    const pmid = normalizePaperPmid(paper);
    if (pmid) {
      const byPmid = papers.find(
        (item) => String(item.pmid || "").trim() === pmid,
      );
      if (byPmid) return byPmid;
    }

    const title = String(paper?.title || "").trim();
    if (!title) return null;
    return (
      papers.find((item) => String(item.title || "").trim() === title) || null
    );
  };

  const renderPaperEngagement = (log, paper) => {
    const engagementState = getSearchEngagementState(log);
    if (engagementState?.loading) {
      return <span className="text-xs text-gray-400">Loading...</span>;
    }
    if (engagementState?.error || engagementState?.notFound) {
      return <span className="text-xs text-gray-400">N/A</span>;
    }

    const paperEngagement = findPaperEngagement(log, paper);
    if (!paperEngagement) {
      return <span className="text-xs text-gray-400">-</span>;
    }

    return (
      <div className="flex flex-wrap gap-1.5">
        {paperEngagement.seen && (
          <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            {t.seen}
          </span>
        )}
        <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
          {t.titleClicks}: {paperEngagement.title_clicks || 0}
        </span>
        <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
          {t.fullTextClicks}: {paperEngagement.full_text_clicks || 0}
        </span>
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
            paperEngagement.bookmarked
              ? "bg-green-50 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {paperEngagement.bookmarked ? t.bookmarked : t.notBookmarked}
        </span>
        {(paperEngagement.bookmark_adds || paperEngagement.bookmark_removes) && (
          <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
            +{paperEngagement.bookmark_adds || 0} / -{paperEngagement.bookmark_removes || 0}
          </span>
        )}
      </div>
    );
  };

  const paperAnalysisMessageCount = (log) => {
    const threadState = paperAnalysisThreads[resolvePaperAnalysisKey(log)];
    const messages = Array.isArray(threadState?.thread?.messages)
      ? threadState.thread.messages
      : [];
    if (messages.length > 0) {
      return messages.filter((message) => message.role === "user").length;
    }
    return log.paper_analysis_question_count;
  };

  // Get unique values for filters
  const uniqueUsers = [
    ...new Set(
      logs.map((log) => log.user_email || log.user_id).filter(Boolean),
    ),
  ];
  // Filter logs based on current filters
  const filteredLogs = logs.filter((log) => {
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "success" && log.success) ||
      (filterStatus === "failed" && !log.success);
    const matchesQuery =
      !filterQuery ||
      log.query.toLowerCase().includes(filterQuery.toLowerCase());
    return (
      matchesStatus &&
      matchesQuery
    );
  });

  // Group logs if needed
  const groupedLogs = React.useMemo(() => {
    if (groupBy === "none") {
      return { "All Logs": filteredLogs };
    }

    const groups = {};
    filteredLogs.forEach((log) => {
      let key;
      if (groupBy === "user") {
        key = log.user_email || log.user_id || "Unknown User";
      } else if (groupBy === "company") {
        key = log.company_name || log.company_id || "Unknown Company";
      }

      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(log);
    });

    return groups;
  }, [filteredLogs, groupBy]);

  const clearFilters = () => {
    setFilterStatus("all");
    setFilterQuery("");
    
    const todayDate = getLocalYYYYMMDD(new Date());
    setFilterStartDate(todayDate);
    setFilterEndDate(todayDate);
    
    setCurrentPage(1);
  };

  // Show access denied if not admin
  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header language={language} setLanguage={setLanguage} />
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <Lock className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-900 mb-2">
              {t.accessDenied}
            </h2>
            <p className="text-red-700">{t.adminRequired}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              {t.backToSearch}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header language={language} setLanguage={setLanguage} />

      {/* Navigation, Page Title and Filter Controls */}
      <div className="bg-white border-b border-gray-100">
        <div className="w-full px-4 py-3 sm:px-6 sm:py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                {t.queryLogs}
              </h2>
              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full flex items-center">
                <Lock className="h-3 w-3 mr-1" />
                {t.adminOnly}
              </span>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={refreshLogs}
                disabled={loading}
                className="flex items-center space-x-2 px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw
                  className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                />
                <span>{t.refreshLogs}</span>
              </button>
              <button
                onClick={exportSearchLogs}
                disabled={exporting || loading}
                className="flex items-center space-x-2 px-3 py-2 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <FileText className="h-4 w-4" />
                <span>{exporting ? t.exportingLogs : t.exportLogs}</span>
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t.backToSearch}
              </button>
            </div>
          </div>

          {/* Filter Controls */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-4">
                  <h3 className="text-sm font-medium text-gray-700">
                    {t.filters}
                  </h3>
                  {filterStartDate && filterEndDate && (
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      {t.viewingLogsFor} {filterStartDate} - {filterEndDate}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-xs font-medium text-gray-600">
                    {t.groupBy}:
                  </label>
                  <select
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                    className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="none">{t.noGrouping}</option>
                    <option value="user">{t.groupByUser}</option>
                    <option value="company">{t.groupByCompany}</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8 gap-4 items-start">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t.startDate}
                  </label>
                  <input
                    type="date"
                    value={filterStartDate}
                    onChange={(e) => setFilterStartDate(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t.endDate}
                  </label>
                  <input
                    type="date"
                    value={filterEndDate}
                    onChange={(e) => setFilterEndDate(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Status Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t.filterByStatus}
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">{t.allStatuses}</option>
                    <option value="success">{t.success}</option>
                    <option value="failed">{t.failed}</option>
                  </select>
                </div>

                {/* Query Filter */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t.searchInQueries}
                  </label>
                  <input
                    type="text"
                    value={filterQuery}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    placeholder={t.searchInQueries}
                    className="w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                <button
                  onClick={() => setDateFilter("today")}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    filterStartDate === getLocalYYYYMMDD(new Date()) &&
                    filterEndDate === getLocalYYYYMMDD(new Date())
                      ? "bg-blue-100 text-blue-700 font-medium border border-blue-200"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                  }`}
                >
                  {t.today}
                </button>
                <button
                  onClick={() => setDateFilter("yesterday")}
                  className={`text-xs px-2 py-1 rounded transition-colors ${
                    (() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 1);
                      const yesterdayDate = getLocalYYYYMMDD(d);
                      return (
                        filterStartDate === yesterdayDate &&
                        filterEndDate === yesterdayDate
                      );
                    })()
                      ? "bg-blue-100 text-blue-700 font-medium border border-blue-200"
                      : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                  }`}
                >
                  {t.yesterday}
                </button>
              </div>

              {/* Clear Filters Button */}
              {(filterStatus !== "all" ||
                filterQuery) && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {t.clearFilters}
                  </button>
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* Analytics Summary */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <Database className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-500">{t.totalQueries}</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {analytics.total_queries || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <Globe className="h-8 w-8 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-500">Unique Users</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {uniqueUsers.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <p className="text-sm text-gray-500">{t.successfulQueries}</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {analytics.successful_queries || 0}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <Clock className="h-8 w-8 text-orange-600" />
                <div>
                  <p className="text-sm text-gray-500">
                    {t.averageProcessingTime}
                  </p>
                  <p className="text-xl font-semibold text-gray-900">
                    {analytics.average_processing_time
                      ? formatDuration(analytics.average_processing_time)
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
              <div className="flex items-center space-x-3">
                <TrendingUp className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="text-sm text-gray-500">{t.totalCost}</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {analytics.total_cost_usd
                      ? formatCost(analytics.total_cost_usd)
                      : "$0.00"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Query Logs Table */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingLogo size="md" message={t.loadingLogs} />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">{t.noLogsFound}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.timestamp}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.user}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.company}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.query}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.analysisQuestions}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.mode}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.credits}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.status}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.processingTime}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.papersFound}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t.cost}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Object.entries(groupedLogs).map(([groupName, groupLogs]) => (
                    <React.Fragment key={groupName}>
                      {groupBy !== "none" && (
                        <tr className="bg-gray-50">
                          <td
                            colSpan="12"
                            className="px-6 py-3 text-sm font-medium text-gray-900"
                          >
                            <div className="flex items-center space-x-2">
                              {groupBy === "user" && (
                                <Users className="h-4 w-4" />
                              )}
                              {groupBy === "company" && (
                                <Building2 className="h-4 w-4" />
                              )}
                              <span>{groupName}</span>
                              <span className="text-gray-500">
                                ({groupLogs.length} logs)
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {groupLogs.map((log, index) => {
                        const rowId = resolveLogRowId(log, index);
                        const isExpanded = expandedRows.has(rowId);
                        return (
                          <React.Fragment key={rowId}>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-4 whitespace-nowrap">
                                <button
                                  onClick={() =>
                                    toggleRowExpansion(log, rowId)
                                  }
                                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                  title={
                                    isExpanded ? t.collapseRow : t.expandRow
                                  }
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </button>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatTimestamp(log.timestamp)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 text-gray-400 mr-1" />
                                  <span className="text-xs">
                                    {log.user_email ||
                                      log.user_id ||
                                      "Anonymous"}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <Building2 className="h-4 w-4 text-gray-400 mr-1" />
                                  <span className="text-xs">
                                    {log.company_name ||
                                      log.company_id ||
                                      "N/A"}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                                <div className="truncate" title={log.query}>
                                  {log.query}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {log.paper_analysis_question_count == null ? (
                                  <span className="text-gray-400">-</span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                                    <MessageSquare className="h-3 w-3" />
                                    {log.paper_analysis_question_count}
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatSearchMode(log.search_mode)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCredits(log.charged_credits)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  {log.success ? (
                                    <CheckCircle className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500" />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${log.success ? "text-green-700" : "text-red-700"}`}
                                  >
                                    {log.success ? t.success : t.failed}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {log.processing_time
                                  ? formatDuration(log.processing_time)
                                  : "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {log.papers_found || 0}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {formatCost(log.total_cost_usd)}
                              </td>
                            </tr>

                            {/* Expanded Details Row */}
                            {isExpanded && (
                              <tr className="bg-gray-50">
                                <td></td>
                                <td colSpan="11" className="px-6 py-4">
                                  <div className="space-y-4">
                                    {/* Complete Query */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <Eye className="h-4 w-4 mr-2" />
                                        {t.completeQuery}
                                      </h4>
                                      <div className="bg-white p-3 rounded border border-gray-200 text-sm text-gray-900 break-words">
                                        {log.query}
                                      </div>
                                    </div>

                                    {/* Query Details Grid */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                                        {t.queryDetails}
                                      </h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {log.ip_address && (
                                          <div className="bg-white p-3 rounded border border-gray-200">
                                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                                              {t.ipAddress}
                                            </div>
                                            <div className="text-sm text-gray-900 mt-1">
                                              {log.ip_address}
                                            </div>
                                          </div>
                                        )}
                                        {log.session_id && (
                                          <div className="bg-white p-3 rounded border border-gray-200">
                                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                                              {t.sessionId}
                                            </div>
                                            <div className="text-sm text-gray-900 mt-1 font-mono text-xs">
                                              {log.session_id}
                                            </div>
                                          </div>
                                        )}
                                        <div className="bg-white p-3 rounded border border-gray-200">
                                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                                            Max Results
                                          </div>
                                          <div className="text-sm text-gray-900 mt-1">
                                            {log.max_results || "N/A"}
                                          </div>
                                        </div>
                                        <div className="bg-white p-3 rounded border border-gray-200">
                                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                                            {t.mode}
                                          </div>
                                          <div className="text-sm text-gray-900 mt-1">
                                            {formatSearchMode(log.search_mode)}
                                          </div>
                                        </div>
                                        <div className="bg-white p-3 rounded border border-gray-200">
                                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                                            {t.credits}
                                          </div>
                                          <div className="text-sm text-gray-900 mt-1">
                                            {formatCredits(log.charged_credits)}
                                          </div>
                                        </div>
                                        {log.query_intent && (
                                          <div className="bg-white p-3 rounded border border-gray-200">
                                            <div className="text-xs text-gray-500 uppercase tracking-wide">
                                              Intent
                                            </div>
                                            <div className="text-sm text-gray-900 mt-1 break-words">
                                              {log.query_intent}
                                            </div>
                                          </div>
                                        )}
                                        <div className="bg-white p-3 rounded border border-gray-200">
                                          <div className="text-xs text-gray-500 uppercase tracking-wide">
                                            {t.preference}
                                          </div>
                                          <div className="text-sm text-gray-900 mt-1">
                                            {formatPreference(
                                              log.preference || log.search_type,
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Result Engagement */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <MousePointerClick className="h-4 w-4 mr-2" />
                                        {t.engagement}
                                      </h4>
                                      {renderSearchEngagementSummary(log)}
                                    </div>

                                    {/* Paper Analysis Engagement */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                                        <MessageSquare className="h-4 w-4 mr-2" />
                                        {t.paperAnalysisEngagement}
                                      </h4>
                                      {renderPaperAnalysisEngagementSummary(log)}
                                    </div>

                                    {/* Paper Analysis Messages */}
                                    <details className="group bg-white border border-gray-200 rounded overflow-hidden">
                                      <summary className="px-3 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 flex items-center justify-between list-none [&::-webkit-details-marker]:hidden">
                                        <span className="flex items-center">
                                          <span className="text-gray-400 mr-2" aria-hidden="true">
                                            <ChevronDown className="h-4 w-4 group-open:hidden" />
                                            <ChevronUp className="h-4 w-4 hidden group-open:block" />
                                          </span>
                                          {t.paperAnalysis}
                                        </span>
                                        <span className="text-xs font-medium text-gray-500">
                                          {(() => {
                                            const count = paperAnalysisMessageCount(log);
                                            if (count == null) return "-";
                                            return `${count} ${count === 1 ? "message" : "messages"}`;
                                          })()}
                                        </span>
                                      </summary>
                                      <div className="border-t border-gray-200 p-3">
                                        {renderPaperAnalysisMessages(log)}
                                      </div>
                                    </details>

                                    {/* Confirmed Criteria Set */}
                                    <div>
                                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                                        {t.selectedCriteria}
                                      </h4>
                                      <div className="bg-white p-3 rounded border border-gray-200">
                                        {(() => {
                                          const criteriaLines = getCriteriaSelectionLines({
                                            criteriaSet: Array.isArray(log.criteria_set)
                                              ? log.criteria_set
                                              : [],
                                            language,
                                          });
                                          if (criteriaLines.length === 0) {
                                            return (
                                              <p className="text-sm text-gray-500">{t.noCriteria}</p>
                                            );
                                          }
                                          return (
                                            <ul className="list-disc list-inside space-y-1">
                                              {criteriaLines.map((line, idx) => (
                                                <li
                                                  key={`${line}-${idx}`}
                                                  className="text-sm text-gray-900 break-words"
                                                >
                                                  {line}
                                                </li>
                                              ))}
                                            </ul>
                                          );
                                        })()}
                                      </div>
                                    </div>

                                    {/* Search Queries Used */}
                                    {log.search_queries_used &&
                                      log.search_queries_used.length > 0 && (
                                        <div>
                                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                                            {t.searchQueriesDetails}
                                          </h4>
                                          <div className="bg-white p-3 rounded border border-gray-200">
                                            <ul className="list-disc list-inside space-y-1">
                                              {log.search_queries_used.map(
                                                (query, idx) => (
                                                  <li
                                                    key={idx}
                                                    className="text-sm text-gray-900 break-words"
                                                  >
                                                    {query}
                                                  </li>
                                                ),
                                              )}
                                            </ul>
                                          </div>
                                        </div>
                                      )}

                                    {/* Paper Results */}
                                    {log.paper_results &&
                                      log.paper_results.length > 0 && (
                                        <details className="group bg-white border border-gray-200 rounded overflow-hidden">
                                          <summary className="px-3 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 flex items-center justify-between list-none [&::-webkit-details-marker]:hidden">
                                            <span>
                                              {t.paperResults} ({log.paper_results.length})
                                            </span>
                                            <span className="text-gray-400" aria-hidden="true">
                                              <ChevronDown className="h-4 w-4 group-open:hidden" />
                                              <ChevronUp className="h-4 w-4 hidden group-open:block" />
                                            </span>
                                          </summary>
                                          <div className="border-t border-gray-200 overflow-hidden">
                                            <table className="w-full text-sm">
                                              <thead className="bg-gray-50">
                                                <tr>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.paperTitle}
                                                  </th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.relevanceScore}
                                                  </th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.paperUrl}
                                                  </th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.engagement}
                                                  </th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    {t.aiDetails}
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-200">
                                                {[...log.paper_results]
                                                  .sort(
                                                    (a, b) =>
                                                      (b.relevance_score || 0) -
                                                      (a.relevance_score || 0),
                                                  )
                                                  .map((paper, idx) => (
                                                    <tr key={idx}>
                                                      <td className="px-3 py-2 text-gray-900 max-w-xs">
                                                        <div
                                                          className="truncate"
                                                          title={paper.title}
                                                        >
                                                          {paper.title ||
                                                            "Untitled"}
                                                        </div>
                                                      </td>
                                                      <td className="px-3 py-2 text-gray-900">
                                                        <span
                                                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                            paper.relevance_score >=
                                                            0.8
                                                              ? "bg-green-100 text-green-800"
                                                              : paper.relevance_score >=
                                                                  0.6
                                                                ? "bg-yellow-100 text-yellow-800"
                                                                : paper.relevance_score >=
                                                                    0.4
                                                                  ? "bg-orange-100 text-orange-800"
                                                                  : "bg-red-100 text-red-800"
                                                          }`}
                                                        >
                                                          {paper.relevance_score
                                                            ? paper.relevance_score.toFixed(
                                                                1,
                                                              )
                                                            : "0.0"}
                                                        </span>
                                                      </td>
                                                      <td className="px-3 py-2 text-gray-900">
                                                        {paper.url ? (
                                                          <a
                                                            href={paper.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-blue-600 hover:text-blue-800 underline text-xs"
                                                          >
                                                            View Paper
                                                          </a>
                                                        ) : (
                                                          <span className="text-gray-400 text-xs">
                                                            No URL
                                                          </span>
                                                        )}
                                                      </td>
                                                      <td className="px-3 py-2 text-gray-900 min-w-52">
                                                        {renderPaperEngagement(log, paper)}
                                                      </td>
                                                      <td className="px-3 py-2 text-gray-900">
                                                        {renderPaperAiDetails(paper) || (
                                                          <span className="text-gray-400 text-xs">
                                                            N/A
                                                          </span>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </details>
                                      )}

                                    {/* No Papers Found Message */}
                                    {log.success &&
                                      (!log.paper_results ||
                                        log.paper_results.length === 0) &&
                                      log.papers_found === 0 && (
                                        <div>
                                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                                            {t.paperResults}
                                          </h4>
                                          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded text-sm text-yellow-800">
                                            {t.noPapersFound}
                                          </div>
                                        </div>
                                      )}

                                    {/* Error Message */}
                                    {log.error_message && (
                                      <div>
                                        <h4 className="text-sm font-medium text-red-700 mb-2">
                                          {t.errorMessage}
                                        </h4>
                                        <div className="bg-red-50 border border-red-200 p-3 rounded text-sm text-red-900 break-words">
                                          {log.error_message}
                                        </div>
                                      </div>
                                    )}

                                    {/* Token Usage */}
                                    {log.token_usage &&
                                      log.token_usage.length > 0 && (
                                        <details className="group bg-white border border-gray-200 rounded overflow-hidden">
                                          <summary className="px-3 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50 flex items-center justify-between list-none [&::-webkit-details-marker]:hidden">
                                            <span>
                                              {t.tokenUsageDetails} ({log.token_usage.length})
                                            </span>
                                            <span className="text-gray-400" aria-hidden="true">
                                              <ChevronDown className="h-4 w-4 group-open:hidden" />
                                              <ChevronUp className="h-4 w-4 hidden group-open:block" />
                                            </span>
                                          </summary>
                                          <div className="border-t border-gray-200 overflow-hidden">
                                            <table className="w-full text-sm">
                                              <thead className="bg-gray-50">
                                                <tr>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Model
                                                  </th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Tokens
                                                  </th>
                                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Cost
                                                  </th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-gray-200">
                                                {log.token_usage.map(
                                                  (usage, idx) => (
                                                    <tr key={idx}>
                                                      <td className="px-3 py-2 text-gray-900">
                                                        {usage.model ||
                                                          "Unknown"}
                                                      </td>
                                                      <td className="px-3 py-2 text-gray-900">
                                                        {usage.total_tokens ||
                                                          0}
                                                      </td>
                                                      <td className="px-3 py-2 text-gray-900">
                                                        {formatCost(
                                                          usage.cost_usd,
                                                        )}
                                                      </td>
                                                    </tr>
                                                  ),
                                                )}
                                              </tbody>
                                            </table>
                                          </div>
                                        </details>
                                      )}

                                    {/* User Agent (collapsed by default due to length) */}
                                    {log.user_agent && (
                                      <details className="bg-white border border-gray-200 rounded">
                                        <summary className="px-3 py-2 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50">
                                          {t.userAgent}
                                        </summary>
                                        <div className="px-3 py-2 border-t border-gray-200 text-xs text-gray-600 break-all">
                                          {log.user_agent}
                                        </div>
                                      </details>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              {totalLogs > 0 && (
                <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    {/* Results info */}
                    <div className="text-sm text-gray-700">
                      Showing{" "}
                      <span className="font-medium">
                        {Math.min((currentPage - 1) * perPage + 1, totalLogs)}
                      </span>{" "}
                      to{" "}
                      <span className="font-medium">
                        {Math.min(currentPage * perPage, totalLogs)}
                      </span>{" "}
                      of <span className="font-medium">{totalLogs}</span> logs
                    </div>

                    {/* Page controls */}
                    <div className="flex items-center space-x-2">
                      {/* Per page selector */}
                      <select
                        value={perPage}
                        onChange={(e) => {
                          setPerPage(Number(e.target.value));
                          setCurrentPage(1); // Reset to first page when changing items per page
                        }}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                      >
                        <option value={10}>10 per page</option>
                        <option value={20}>20 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                      </select>

                      {/* Previous button */}
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className={`px-3 py-1 border rounded-md text-sm ${
                          currentPage <= 1
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Previous
                      </button>

                      {/* Page numbers */}
                      <div className="flex items-center space-x-1">
                        {/* Show page numbers intelligently */}
                        {totalPages <= 7 ? (
                          // Show all pages if 7 or less
                          Array.from(
                            { length: totalPages },
                            (_, i) => i + 1,
                          ).map((pageNum) => (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`px-3 py-1 border rounded-md text-sm ${
                                pageNum === currentPage
                                  ? "bg-blue-600 text-white"
                                  : "bg-white text-gray-700 hover:bg-gray-50"
                              }`}
                            >
                              {pageNum}
                            </button>
                          ))
                        ) : (
                          // Show smart pagination for many pages
                          <>
                            {currentPage > 2 && (
                              <>
                                <button
                                  onClick={() => setCurrentPage(1)}
                                  className="px-3 py-1 border rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
                                >
                                  1
                                </button>
                                {currentPage > 3 && (
                                  <span className="px-2">...</span>
                                )}
                              </>
                            )}

                            {/* Current page and neighbors */}
                            {[currentPage - 1, currentPage, currentPage + 1]
                              .filter(
                                (pageNum) =>
                                  pageNum > 0 && pageNum <= totalPages,
                              )
                              .map((pageNum) => (
                                <button
                                  key={pageNum}
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`px-3 py-1 border rounded-md text-sm ${
                                    pageNum === currentPage
                                      ? "bg-blue-600 text-white"
                                      : "bg-white text-gray-700 hover:bg-gray-50"
                                  }`}
                                >
                                  {pageNum}
                                </button>
                              ))}

                            {currentPage < totalPages - 1 && (
                              <>
                                {currentPage < totalPages - 2 && (
                                  <span className="px-2">...</span>
                                )}
                                <button
                                  onClick={() => setCurrentPage(totalPages)}
                                  className="px-3 py-1 border rounded-md text-sm bg-white text-gray-700 hover:bg-gray-50"
                                >
                                  {totalPages}
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </div>

                      {/* Next button */}
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className={`px-3 py-1 border rounded-md text-sm ${
                          currentPage >= totalPages
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "bg-white text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default QueryLogsPage;
